/**
 * Notification types for the in-app notification system.
 */

/**
 * Mirrors the `type` strings the backend writes. `join_request` is admin-facing:
 * NotificationService.notifyAdminsOfJoinRequest creates one per active admin
 * when someone asks to join the organization.
 */
export type NotificationType = 'course' | 'achievement' | 'reminder' | 'system' | 'join_request'

export interface Notification {
  id: string
  userId: string
  tenantId?: string
  type: NotificationType
  title: string
  message: string
  read: boolean
  actionUrl?: string
  metadata?: Record<string, unknown>
  createdAt: string
  readAt?: string
}

/**
 * Outcome of a decided join request, mirrored from the backend's
 * `metadata.outcome`.
 */
export type JoinRequestOutcome = 'approved' | 'rejected'

/**
 * The resolved state a `join_request` notification carries once an admin has
 * approved or declined it. NotificationService.resolveJoinRequestNotifications
 * rewrites the row in place rather than deleting it, so the bell shows the
 * decision as history instead of still asking for action.
 */
export interface ResolvedJoinRequest {
  outcome: JoinRequestOutcome
  /** Display name of the admin who decided it, or "an admin". */
  resolvedBy?: string
}

/**
 * Read the resolved state off a notification, or null while it is still
 * actionable. Metadata is an untyped JSONB payload, so this is the one place
 * that narrows it.
 */
export function getResolvedJoinRequest(notification: Notification): ResolvedJoinRequest | null {
  const metadata = notification.metadata
  if (!metadata || metadata.resolved !== true) return null
  const outcome = metadata.outcome === 'rejected' ? 'rejected' : 'approved'
  const resolvedBy = typeof metadata.resolvedBy === 'string' ? metadata.resolvedBy : undefined
  return { outcome, resolvedBy }
}

export interface NotificationApiError {
  message: string
  status?: number
}
